const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const HOURS_PER_DAY = 24;
const MINUTES_PER_HOUR = 60;

/** Which unit a countdown ended up being said in. */
export enum TodayCountdownUnit {
  days = 'days',
  hours = 'hours',
  minutes = 'minutes',
}

/** How long is left, in the one unit worth saying it in. */
export interface TodayCountdown {
  unit: TodayCountdownUnit;
  /** Always at least 1: a countdown never reads "in 0". */
  value: number;
}

/**
 * How long until something unlocks, in the coarsest unit that still says something (IPD-3052).
 *
 * It returns the shape and lets the caller phrase it: this decides the unit and the rounding, the
 * component owns the wording, so the same rule can serve copy that talks about assessments and copy
 * that talks about sessions without either borrowing the other's vocabulary.
 *
 * DELIBERATE DUPLICATE, and worth knowing before "fixing" it. `session-unlocks.vue` applies the same
 * three-step rule inline for a locked session, and unifying the two would mean that component
 * importing this file — which pulls this file into the medical device inventory, because everything
 * reachable from `src/vue/pages/interventions/` is in scope (MEDICAL_DEVICE_MANIFEST.md, in-scope
 * criterion 3). Every later change to a Today countdown would then be a change to the device.
 * That is a real price and this ticket is not the place to pay it, so the rules are kept side by
 * side on purpose. Unifying later is one import there plus a regenerated lock.
 *
 * The minutes step matches that component exactly. The hours step no longer does, and deliberately:
 * a rounded 24 is said here as a day, where a session says "24 hours". A session has no days step to
 * promote to — past a day it shows a date — so 24 hours is the most it can say; this file does have
 * one, and it already promotes a rounded 60 minutes to an hour, so not promoting a rounded 24 hours
 * was inconsistent with its own reasoning rather than faithful to the session's.
 *
 * Where the two differ, and why: past a day a session shows the absolute date it unlocks, and here a
 * number of days is said instead ("Unlocks in XX days", IPD-2905 state 4). That step exists for a
 * session because a session can sit locked for weeks, which is exactly what does not happen on this
 * page — nothing on the Today is ever more than three days out, since the backend drops this tile
 * past that. Over a horizon that short a count of days says more than a date does.
 *
 * The open question is not this card against a session, it is this card against the Upcoming tile,
 * which announces something arriving within the day as "unlocks on {date}": the nearer thing shows a
 * date and the further one shows days, which is the wrong way round. It is copy rather than
 * contract, so it is design's to settle across both cards at once; if it settles the other way, the
 * days step becomes a date step and nothing else moves.
 *
 * @param remainingMillis how long is left. Null, zero or negative means there is nothing to say:
 *   the moment has passed, or the item was never counting down to begin with.
 */
export function resolveTodayUnlockCountdown(remainingMillis: number | null): TodayCountdown | null {
  if (remainingMillis === null || remainingMillis <= 0) {
    return null;
  }

  // Rounded to the nearest rather than up, at every step, for the reason the session component
  // states for its own: 25 hours left reads as "1 day", where rounding up would claim two.
  if (remainingMillis >= DAY_MS) {
    return {unit: TodayCountdownUnit.days, value: Math.round(remainingMillis / DAY_MS)};
  }
  if (remainingMillis > HOUR_MS) {
    const hours = Math.round(remainingMillis / HOUR_MS);
    // A rounded 24 is a day, and saying "24 hours" for it reads like a longer wait than "1 day" does.
    // The same promotion the minutes step below already makes for a rounded 60.
    return hours === HOURS_PER_DAY
      ? {unit: TodayCountdownUnit.days, value: 1}
      : {unit: TodayCountdownUnit.hours, value: hours};
  }

  // Within the hour, rounded up instead, so it never counts down to "0 minutes" while still locked.
  // A rounded-up 60 reads better as an hour than as sixty minutes.
  const minutes = Math.ceil(remainingMillis / MINUTE_MS);
  return minutes === MINUTES_PER_HOUR
    ? {unit: TodayCountdownUnit.hours, value: 1}
    : {unit: TodayCountdownUnit.minutes, value: minutes};
}
